/**
 * transcript.js — «запись → текст» для /screen-recorder.
 *
 * ЗАЧЕМ ЭТО УСТРОЕНО ИМЕННО ТАК
 * ------------------------------
 * У всех облачных конкурентов (Loom, ScreenApp, Veed, Tella) расшифровка —
 * ВТОРОЙ акт: запись закончилась → файл уехал на сервер → человек ждёт.
 * Здесь звук снимается ПАРАЛЛЕЛЬНО записи, прямо с того сумматора, который
 * слышит кодировщик, и к моменту нажатия «Стоп» PCM уже готов. Поэтому:
 *
 *   • не надо декодировать готовый MP4 ради дорожки (для часового файла это
 *     минуты работы и сотни мегабайт пиковой памяти);
 *   • расшифровка стартует мгновенно по «Стоп»;
 *   • слышно ровно то, что попало в файл — вместе с положением ползунка
 *     громкости микрофона.
 *
 * ⛔ Сама расшифровка НЕ идёт во время записи СОЗНАТЕЛЬНО. Whisper рядом с
 * WebCodecs конкурирует за те же ядра, а уронить кадры в файле ради текста —
 * это разменять продукт на бонус. Тап (см. transcript-worklet.js) живёт на
 * аудиопотоке и стоит доли процента CPU; модель запускается только после
 * остановки.
 *
 * ПАМЯТЬ. PCM копится как Int16 (32 КБ/с). Бюджет считается от deviceMemory;
 * при его исчерпании тап ЧЕСТНО останавливается и помечает `truncated`, а не
 * молча роняет вкладку. Молчаливое усечение здесь было бы худшим вариантом:
 * человек получил бы текст, обрывающийся без объяснений.
 */
(function () {
    'use strict';

    var TARGET_RATE = 16000;

    /* Окно расшифровки. 120 с — компромисс: короче → чаще швы и потери
       контекста у модели, длиннее → позднее первый видимый результат и выше
       пик памяти (Float32 окна = 7.7 МБ на 120 с). */
    var WINDOW_SEC = 120;
    /* Границу окна ищем в самом тихом месте ±3 с от номинальной — так шов
       почти никогда не режет слово пополам. Дёшево: RMS по готовому PCM. */
    var SEAM_SEARCH_SEC = 3;

    var FIR_TAPS = 65;
    var FIR_CUTOFF_HZ = 6000;

    /* Тот же ФНЧ, что в transcript-worklet.js — окно Блэкмана + sinc.
       Двухполюсный IIR, стоявший здесь до 2026-08-08, давал на 9 кГц всего
       ~7 дБ подавления, и стенд поймал зеркало на 7 кГц амплитудой 0.21. */
    function buildFir(rate) {
        var h = new Float32Array(FIR_TAPS);
        var m = (FIR_TAPS - 1) / 2;
        var wc = 2 * Math.PI * Math.min(FIR_CUTOFF_HZ, rate * 0.45) / rate;
        var sum = 0, i;
        for (i = 0; i < FIR_TAPS; i++) {
            var n = i - m;
            var sinc = n === 0 ? wc / Math.PI : Math.sin(wc * n) / (Math.PI * n);
            var w = 0.42 - 0.5 * Math.cos(2 * Math.PI * i / (FIR_TAPS - 1))
                + 0.08 * Math.cos(4 * Math.PI * i / (FIR_TAPS - 1));
            h[i] = sinc * w;
            sum += h[i];
        }
        for (i = 0; i < FIR_TAPS; i++) h[i] /= sum;
        return h;
    }

    function memBudgetBytes() {
        var gb = 4;
        try { if (navigator.deviceMemory) gb = navigator.deviceMemory; } catch (_) {}
        /* 32 МБ на гигабайт устройства, но не меньше 48 и не больше 320.
           48 МБ Int16 ≈ 25 минут речи, 320 МБ ≈ 2 ч 50 мин. */
        return Math.max(48, Math.min(320, Math.round(gb * 32))) * 1024 * 1024;
    }

    /* ─────────────────────────── PCM-тап ─────────────────────────── */

    function createTap(mixer, opts) {
        opts = opts || {};
        var ctx = mixer && mixer.ctx;
        var bus = mixer && (mixer.mixBus || mixer.dest);
        /* Конвейер WebCodecs с 2026-09-02 отдаёт звук не графом Web Audio, а
           готовыми PCM-блоками (audio-timeline.js, mixer.subscribe). Тап тогда
           живёт на главном потоке; это не роняет кадры: блоки приходят уже
           после того, как их получил кодировщик, и 65-тапный ФНЧ на 48 кГц
           стоит ~3 млн умножений в секунду. */
        var pcmFeed = (mixer && typeof mixer.subscribe === 'function') ? mixer : null;
        if (!pcmFeed && (!ctx || !bus)) return null;
        var unsubscribe = null;

        var chunks = [];          // Int16Array
        var samples = 0;          // всего 16 кГц-сэмплов собрано
        var budget = memBudgetBytes();
        var truncated = false;
        var node = null;
        var fallbackNode = null;
        var active = true;
        var destroyed = false;
        var pendingFlush = null;
        /* true, как только ОДИН из двух путей съёма подключён к шине. */
        var attached = false;

        function absorb(f32) {
            if (destroyed || truncated) return;
            if (samples * 2 >= budget) {
                truncated = true;
                setActive(false);
                if (opts.onTruncate) try { opts.onTruncate(samples / TARGET_RATE); } catch (_) {}
                return;
            }
            var i16 = new Int16Array(f32.length);
            for (var i = 0; i < f32.length; i++) {
                var s = f32[i];
                if (s > 1) s = 1; else if (s < -1) s = -1;
                i16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            chunks.push(i16);
            samples += i16.length;
            if (opts.onProgress) try { opts.onProgress(samples / TARGET_RATE); } catch (_) {}
        }

        function setActive(v) {
            active = !!v;
            try { if (node) node.port.postMessage({ type: 'active', value: active }); } catch (_) {}
        }

        function attachWorklet() {
            var url = window.__srWorkerBase && window.__srWorkerBase.transcriptWorklet;
            if (!url || !ctx.audioWorklet) return Promise.reject(new Error('no_worklet'));
            return ctx.audioWorklet.addModule(url).then(function () {
                /* Пока модуль качался, мог подключиться запасной путь. Тогда
                   ворклет НЕ подключаем: два тапа на одной шине посчитали бы
                   звук дважды и удвоили длительность расшифровки. */
                if (attached) return;
                node = new AudioWorkletNode(ctx, 'sr-transcript-tap', {
                    numberOfInputs: 1, numberOfOutputs: 0
                });
                node.port.onmessage = function (e) {
                    if (!e.data || e.data.type !== 'pcm') return;
                    absorb(e.data.pcm);
                    if (e.data.flushed && pendingFlush) { var f = pendingFlush; pendingFlush = null; f(); }
                };
                bus.connect(node);
            });
        }

        /* ScriptProcessor — устаревший, но универсальный запасной путь. Живёт
           на главном потоке, поэтому используется ТОЛЬКО когда AudioWorklet
           недоступен.
           ⚠️ ФИЛЬТР ЗДЕСЬ ОБЯЗАН БЫТЬ ТОТ ЖЕ, что в ворклете: два разных
           ресемплера — это два разных качества расшифровки на соседних
           браузерах, и расхождение никто не заметит глазами. Стенд
           screen-recorder-transcript-tap.mjs гоняет ОБА пути, чтобы копия не
           разъехалась молча. */
        /* ФНЧ + децимация до 16 кГц — ОДНА функция для обоих путей на
           главном потоке (ScriptProcessor и PCM-блоки таймлайна). */
        function makeDecimator(rate) {
            var ratio = rate / TARGET_RATE;
            var pos = 0, prev = 0;
            var h = buildFir(rate);
            var hist = new Float32Array(FIR_TAPS);
            var hi = 0;
            return function (input) {
                var out = new Float32Array(Math.ceil(input.length / ratio) + 2);
                var n = 0;
                for (var i = 0; i < input.length; i++) {
                    hist[hi] = input[i];
                    hi = (hi + 1) % FIR_TAPS;
                    var acc = 0, k = hi;
                    for (var t = 0; t < FIR_TAPS; t++) { acc += h[t] * hist[k]; k = (k + 1) % FIR_TAPS; }
                    while (pos < 1) { out[n++] = prev + (acc - prev) * pos; pos += ratio; }
                    pos -= 1; prev = acc;
                }
                return out.subarray(0, n);
            };
        }
        function attachPcmFeed() {
            var dec = null, decRate = 0;
            unsubscribe = pcmFeed.subscribe(function (planes, frames, rate) {
                if (!active || destroyed || !planes || !planes.length) return;
                if (!dec || decRate !== rate) { dec = makeDecimator(rate); decRate = rate; }
                var mono = new Float32Array(frames);
                for (var c = 0; c < planes.length; c++) { var p = planes[c]; for (var i = 0; i < frames; i++) mono[i] += p[i]; }
                if (planes.length > 1) { var inv = 1 / planes.length; for (i = 0; i < frames; i++) mono[i] *= inv; }
                var out = dec(mono);
                if (out.length) absorb(out);
            });
        }
        function attachScriptProcessor() {
            var dec = makeDecimator(ctx.sampleRate);
            fallbackNode = ctx.createScriptProcessor(4096, 1, 1);
            fallbackNode.onaudioprocess = function (ev) {
                if (!active || destroyed) return;
                var out = dec(ev.inputBuffer.getChannelData(0));
                if (out.length) absorb(out);
            };
            bus.connect(fallbackNode);
            /* ScriptProcessor не тикает, пока не подключён к назначению.
               Нулевой gain — чтобы тап не был слышен в колонках. */
            var sink = ctx.createGain();
            sink.gain.value = 0;
            fallbackNode.connect(sink);
            sink.connect(ctx.destination);
        }

        /* ⚠️ Гонка со скачиванием модуля. addModule() на холодном кэше идёт в
           сеть уже ПОСЛЕ старта записи, и всё это время не собирается ничего
           — на проде так терялся весь короткий дубль (замер 08.08). Поэтому
           не ждём ворклет молча: через 400 мс подключаем ScriptProcessor и
           начинаем собирать. Если ворклет доедет позже, он просто не нужен —
           второй тап не подключаем, иначе звук считался бы дважды. */
        function claim(fn) {
            if (attached) return;
            attached = true;
            fn();
        }
        var ready = new Promise(function (resolve) {
            if (pcmFeed) {
                claim(function () { try { attachPcmFeed(); } catch (_) {} });
                resolve();
                return;
            }
            var settled = false;
            var timer = setTimeout(function () {
                if (settled) return;
                settled = true;
                claim(function () { try { attachScriptProcessor(); } catch (_) {} });
                resolve();
            }, 400);
            attachWorklet().then(function () {
                clearTimeout(timer);
                if (settled) return;   // ScriptProcessor уже пишет — ворклет опоздал
                settled = true;
                attached = true;
                resolve();
            }).catch(function () {
                clearTimeout(timer);
                if (settled) return;
                settled = true;
                claim(function () { try { attachScriptProcessor(); } catch (_) {} });
                resolve();
            });
        });

        function collect() {
            var total = samples;
            var out = new Float32Array(total);
            var o = 0;
            for (var c = 0; c < chunks.length; c++) {
                var ch = chunks[c];
                for (var i = 0; i < ch.length; i++) out[o++] = ch[i] / 0x8000;
            }
            return out;
        }

        /* Досылает хвост, застрявший в незаполненном блоке ворклета.
           Без этого КАЖДАЯ расшифровка теряла до 0.26 с в конце — стенд
           показал 2.816 с вместо 3.000 с, и это ровно тот дефект, который в
           реальном тексте выглядит как «последнее слово не распозналось». */
        function flush() {
            if (!node || destroyed) return Promise.resolve();
            return new Promise(function (resolve) {
                var done = false;
                pendingFlush = function () { if (!done) { done = true; resolve(); } };
                try { node.port.postMessage({ type: 'flush' }); } catch (_) { pendingFlush = null; resolve(); return; }
                setTimeout(function () { if (!done) { done = true; pendingFlush = null; resolve(); } }, 400);
            });
        }

        return {
            ready: ready,
            flush: flush,
            setActive: setActive,
            capturedSec: function () { return samples / TARGET_RATE; },
            wasTruncated: function () { return truncated; },
            /* Отдаёт Float32 16 кГц моно и освобождает накопитель. */
            take: function () {
                var pcm = collect();
                chunks = [];
                return { pcm: pcm, durationSec: pcm.length / TARGET_RATE, truncated: truncated };
            },
            destroy: function () {
                destroyed = true;
                chunks = [];
                samples = 0;
                try { if (node) { bus.disconnect(node); node.port.onmessage = null; } } catch (_) {}
                try { if (fallbackNode) { bus.disconnect(fallbackNode); fallbackNode.onaudioprocess = null; } } catch (_) {}
                try { if (unsubscribe) unsubscribe(); } catch (_) {}
                unsubscribe = null;
                node = null; fallbackNode = null;
            }
        };
    }

    /* ─────────────────────── Расшифровка окнами ─────────────────────── */

    /* Ищем самый тихий сэмпл-индекс вблизи номинальной границы, чтобы шов
       между окнами не приходился на середину слова. */
    function findSeam(pcm, nominal) {
        var span = SEAM_SEARCH_SEC * TARGET_RATE;
        var from = Math.max(0, nominal - span);
        var to = Math.min(pcm.length, nominal + span);
        if (to - from < TARGET_RATE) return nominal;
        var win = Math.round(TARGET_RATE * 0.05);   // 50 мс
        var best = nominal, bestE = Infinity;
        for (var i = from; i + win < to; i += win) {
            var e = 0;
            for (var j = i; j < i + win; j++) e += pcm[j] * pcm[j];
            if (e < bestE) { bestE = e; best = i + (win >> 1); }
        }
        return best;
    }

    function planWindows(pcm) {
        var out = [];
        var step = WINDOW_SEC * TARGET_RATE;
        var cursor = 0;
        while (cursor < pcm.length) {
            var nominal = cursor + step;
            if (nominal >= pcm.length - TARGET_RATE) { out.push([cursor, pcm.length]); break; }
            var seam = findSeam(pcm, nominal);
            if (seam <= cursor + TARGET_RATE) seam = nominal;   // защита от вырождения
            out.push([cursor, seam]);
            cursor = seam;
        }
        return out;
    }

    function transcribe(o) {
        var pcm = o.pcm;
        var onProgress = o.onProgress || function () {};
        var onPartial = o.onPartial || function () {};
        var modelSize = o.modelSize || 'base';
        var language = o.language || 'auto';

        if (!pcm || pcm.length < TARGET_RATE * 0.4) {
            return Promise.resolve({ segments: [], text: '', empty: true });
        }

        /* ⚠️ `locale` и `hfmirror` в URL воркера — НЕ украшения.
           whisper-worker.js читает `?locale=` из собственного адреса и по нему
           выбирает ПОРЯДОК зеркал HuggingFace; без параметра он считает себя
           английским, и RU-шард пошёл бы за весами не к тем хостам.
           `hfmirror` включает наше зеркало — оно есть только для tiny и base,
           поэтому и выбор моделей здесь ограничен этими двумя. */
        var base = (window.__srWorkerBase && window.__srWorkerBase.whisper) || '/js/whisper-worker.js';
        var sep = base.indexOf('?') === -1 ? '?' : '&';
        var locale = (window.__LOCALE ? String(window.__LOCALE) : 'en').toLowerCase();
        var url = base + sep + 'locale=' + encodeURIComponent(locale);
        if (modelSize === 'tiny' || modelSize === 'base') {
            url += '&hfmirror=' + encodeURIComponent('/vendor/whisper-hf/');
        }
        var worker;
        try { worker = new Worker(url); } catch (e) { return Promise.reject(e); }

        var windows = planWindows(pcm);
        var segments = [];
        var idx = 0;
        var cancelled = false;

        function runWindow() {
            if (cancelled) return Promise.resolve();
            if (idx >= windows.length) return Promise.resolve();
            var w = windows[idx];
            var offsetSec = w[0] / TARGET_RATE;
            var slice = pcm.slice(w[0], w[1]);

            return new Promise(function (resolve, reject) {
                function cleanup() { worker.onmessage = null; worker.onerror = null; }
                worker.onerror = function (e) { cleanup(); reject(new Error(e.message || 'whisper_worker_error')); };
                worker.onmessage = function (e) {
                    var m = e.data || {};
                    if (m.type === 'progress') {
                        var within = Math.max(0, Math.min(1, m.progress != null ? m.progress : 0));
                        onProgress((idx + within) / windows.length);
                        return;
                    }
                    if (m.type === 'error') { cleanup(); reject(new Error(m.message || 'whisper_error')); return; }
                    if (m.type === 'result') {
                        cleanup();
                        var chunks = (m.result && m.result.chunks) || [];
                        for (var i = 0; i < chunks.length; i++) {
                            var ts = chunks[i].timestamp || [0, 0];
                            var text = (chunks[i].text || '').trim();
                            if (!text) continue;
                            segments.push({
                                start: offsetSec + (ts[0] || 0),
                                end: offsetSec + (ts[1] || ts[0] || 0),
                                text: text
                            });
                        }
                        onPartial(segments.slice());
                        onProgress((idx + 1) / windows.length);
                        resolve();
                    }
                };
                worker.postMessage({
                    type: 'transcribe',
                    audioBuffer: slice.buffer,
                    modelSize: modelSize,
                    language: language,
                    wordTimestamps: false
                }, [slice.buffer]);
            }).then(function () {
                idx++;
                return runWindow();
            });
        }

        return runWindow().then(function () {
            try { worker.terminate(); } catch (_) {}
            /* Сегменты без конца (Whisper иногда отдаёт [t, null]) чиним по
               следующему началу — иначе SRT получит нулевую длительность и
               плеер проглотит реплику. */
            for (var i = 0; i < segments.length; i++) {
                if (!(segments[i].end > segments[i].start)) {
                    segments[i].end = (i + 1 < segments.length)
                        ? Math.max(segments[i].start + 0.4, segments[i + 1].start - 0.05)
                        : segments[i].start + 1.6;
                }
            }
            return {
                segments: segments,
                text: segments.map(function (s) { return s.text; }).join(' ').trim(),
                empty: segments.length === 0
            };
        }).catch(function (err) {
            try { worker.terminate(); } catch (_) {}
            throw err;
        });
    }

    /* ───────────────────────────── Экспорт ───────────────────────────── */

    function pad(n, w) { n = String(Math.floor(n)); while (n.length < w) n = '0' + n; return n; }

    function stamp(sec, comma) {
        if (!(sec >= 0)) sec = 0;
        var total = Math.round(sec * 1000);   // whole ms first: 3.9996 s is 00:00:04.000, not 00:00:03.1000
        var ms = total % 1000;
        var s = Math.floor(total / 1000);
        return pad(s / 3600, 2) + ':' + pad((s % 3600) / 60, 2) + ':' + pad(s % 60, 2) +
            (comma ? ',' : '.') + pad(ms, 3);
    }

    function toTxt(segments) {
        return segments.map(function (s) { return s.text; }).join('\n');
    }

    function toSrt(segments) {
        return segments.map(function (s, i) {
            return (i + 1) + '\n' + stamp(s.start, true) + ' --> ' + stamp(s.end, true) + '\n' + s.text + '\n';
        }).join('\n');
    }

    function toVtt(segments) {
        return 'WEBVTT\n\n' + segments.map(function (s) {
            return stamp(s.start, false) + ' --> ' + stamp(s.end, false) + '\n' + s.text + '\n';
        }).join('\n');
    }

    window.SRTranscript = {
        createTap: createTap,
        transcribe: transcribe,
        toTxt: toTxt,
        toSrt: toSrt,
        toVtt: toVtt,
        _planWindows: planWindows,
        _stamp: stamp,
        TARGET_RATE: TARGET_RATE
    };
})();
